import React, { Component, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import { authService } from '../../services/authService';

import {
  Card,
  Row,
  Col,
  Collapse,
  Spin,
  Tag,
  Table,
  Button,
  message,
  Grid
} from 'antd';

import {
  FileTextOutlined,
  FilePdfOutlined,
  SisternodeOutlined
} from '@ant-design/icons';

import Addon from './Addon';
import { generateReportPDF } from './generateReportPDF';
import { logo } from '../utilities';
import { API_BASE_URL } from '../../config/api';

const { Panel } = Collapse;
const { useBreakpoint } = Grid;

class AccessionDetail extends Component {
  constructor(props){
    super(props);
    this.state = {
      item: {},
      loading: true,

      addonItem: {},
      addTest: false,

      generatingReport: false,
      selectedTestCodes: [],
    }
  }

  onGenerateReport = async () => {
    const { accessionID } = this.props;

    // Names of the selected performed tests — these match the report's panel headers
    const selectedNames = new Set(
      (this.state.item.tests || [])
        .filter(t => this.state.selectedTestCodes.includes(t.code))
        .map(t => (t.name || '').trim())
    );
    if (!selectedNames.size) {
      message.warning('Select at least one test to include in the report');
      return;
    }

    this.setState({ generatingReport: true });
    try {
      const token = await authService.ensureValidToken();
      if (!token) {
        message.error('Not authenticated');
        return;
      }
      const res = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/accessions/report-data/${accessionID}`,
        headers: { Authorization: `Bearer ${token}` },
      });
      // keep only results belonging to the selected tests
      const results = (res.data?.results || []).filter(
        r => selectedNames.has((r.panel || '').trim())
      );
      if (!results.length) {
        message.warning('No reported results found for the selected tests');
        return;
      }
      const doc = generateReportPDF({ ...res.data, results }, logo);
      window.open(doc.output('bloburl'), '_blank');
    } catch (err) {
      console.log(err);
      message.error('Failed to generate report');
    } finally {
      this.setState({ generatingReport: false });
    }
  }

  componentDidMount() {
    this.loadData();
  }

  deduplicateTests = (tests) => {
    // filter out cancelled first
    const active = tests.filter(t => t.status !== 'Cancelled');

    // group by code
    const grouped = {};
    active.forEach(t => {
      if (!grouped[t.code]) {
        grouped[t.code] = [];
      }
      grouped[t.code].push(t);
    });

    // merge each group into one
    return Object.values(grouped).map(group => {
      // take first item as base
      const base = { ...group[0] };

      // status: if any is Pending, it's Pending
      if (group.some(t => t.status === 'Pending')) {
        base.status = 'Pending';
      }

      // flag: critical takes priority over abnormal
      if (group.some(t => t.flag === 'Critical High' || t.flag === 'Critical Low' || t.flag === 'CL' || t.flag === 'CH' || t.flag === 'C')) {
        base.flag = group.find(t => t.flag === 'Critical High' || t.flag === 'Critical Low' || t.flag === 'CL' || t.flag === 'CH' || t.flag === 'C').flag;
      } else if (group.some(t => t.flag === 'Abnormal' || t.flag === 'High' || t.flag === 'Low' || t.flag === 'L' || t.flag === 'H' || t.flag === 'A')) {
        base.flag = group.find(t => t.flag === 'Abnormal' || t.flag === 'High' || t.flag === 'Low' || t.flag === 'L' || t.flag === 'H' || t.flag === 'A').flag;
      }

      return base;
    });
  };

  loadData = async () => {
    const { accessionID } = this.props;
    this.setState({loading: true})
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/accessions/result/${accessionID}`,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const tests = this.deduplicateTests(res.data.tests);
      this.setState({
        item: {
          ...res.data,
          tests,
        },
        // default: no tests selected — user picks which to include
        selectedTestCodes: [],
        loading: false
      })
    }).catch(err =>{
      console.log(err);
    })
  }

  onAddTest = (item) => {
    this.setState({
      addTest: !this.state.addTest,
      addonItem: item,
    })
  }

  // Opening the report counts as the practice reviewing it (per-client scope).
  // Silent background action — failing quietly just leaves it needing review.
  markReviewed = async () => {
    const clientCode = this.props.client?.ClientCode;
    if (!clientCode) return;
    const token = await authService.ensureValidToken();
    if (!token) return;
    const u = authService.getUserAttributes() || {};
    const reviewedBy = u.name || u.preferred_username || u.email || '';
    try {
      await axios({
        method: 'POST',
        url: `${API_BASE_URL}/connect/accessions/${this.props.accessionID}/review`,
        data: { clientCode, reviewedBy },
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.log(err);
    }
  }

  // Fetch the report PDF WITH the auth header, then open it as a blob. A plain
  // <a href> navigates without the Bearer token, so the protected endpoint
  // returns 401. The blank tab is opened synchronously so popups aren't blocked.
  viewReport = async () => {
    const win = window.open('', '_blank');
    try {
      const token = await authService.ensureValidToken();
      if (!token) {
        message.error('Not authenticated');
        if (win) win.close();
        return;
      }
      const res = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/accessions/report/${this.props.accessionID}`,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) win.location = blobUrl;
      else window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      this.markReviewed();
    } catch (err) {
      if (win) win.close();
      console.log(err);
      message.error('Failed to open report');
    }
  }

  render() {

    const isSmall = !this.props.screens?.lg;

    const shortFlag = (label) => {
      if (!isSmall) return label;
      if (label === 'Abnormal') return 'ABN';
      if (label === 'Critical') return 'CRT';
      return label;
    };

    const shortStatus = (status) => {
      if (!status) return '';
      if (!isSmall) return status;
      if (status === 'Preliminary') return 'PRE';
      if (status === 'Pending') return 'PND';
      if (status === 'Final') return 'FIN';
      return status;
    };

    const testColumns = [
      {
        title: 'Test Code',
        dataIndex: 'code',
        responsive: ['md'],
        key: 'code',
      },
      {
        title: 'Test Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Flag',
        dataIndex: 'flag',
        key: 'flag',
        render: (flag) => {
          if (!flag || flag === 'Normal' || flag === 'Negative' || flag === 'N') return null;
          
          const isCritical = flag === 'Critical High' || flag === 'Critical Low' || flag === 'C' || flag === 'CL' || flag === 'CH';
          const isAbnormal = flag === 'High' || flag === 'Low' || flag === 'Abnormal' || flag === 'A' || flag === 'L' || flag === 'H';

          const color = isCritical ? 'red' : isAbnormal ? 'yellow' : 'default';
          const label = isCritical ? 'Critical' : isAbnormal ? 'Abnormal' : flag;

          return <Tag color={color} style={isSmall ? { padding: '0 6px' } : undefined}>{shortFlag(label)}</Tag>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <Tag color={status === 'Final' ? 'blue' : 'orange'} style={isSmall ? { padding: '0 6px' } : undefined}>{shortStatus(status)}</Tag>
        ),
      },
    ];

    return (
      <Card title={`Accession ${this.props.accessionID}`} style={{ width: '100%' }}>
        {this.state.loading ?
          <Spin/> :
          <Fragment>
            <Collapse
              size="small"
              defaultActiveKey={['order', 'results']}
            >
              <Panel
                header={<strong>Order Details:</strong>}
                key="order"
                style={{ background: '#e6f4ff', borderRadius: 8}}
              >
                <Row gutter={[16, 6]}>
                  <Col xs={24} sm={12} md={7}>
                    <strong>Patient: </strong>
                    {this.state.item.patient.lastName}, {this.state.item.patient.firstName}
                  </Col>
                  <Col xs={24} sm={12} md={5}>
                    <strong>DOB: </strong>
                    {moment(this.state.item.patient.dob, 'YYYY-MM-DD').format("MM/DD/YYYY")}
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <strong>Gender: </strong>{this.state.item.patient.sex}
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <strong>Collected: </strong>
                    {moment(this.state.item.collectionDate, 'YYYY-MM-DD HH:mm:ss').format("MM/DD/YYYY HH:mm A")}
                  </Col>
                  <Col xs={24}>
                    <strong>Status: </strong>
                    <Tag
                      color={
                        this.state.item.status === 'Final'
                          ? 'blue'
                          : this.state.item.status === 'Pending'
                          ? 'orange'
                          : 'default'
                      }
                    >
                      {this.state.item.status}
                    </Tag>
                  </Col>
                  <Col xs={24}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <Button type="primary" size="small" icon={<FileTextOutlined />} onClick={this.viewReport}>
                        View Report
                      </Button>
                      <Button
                        type="primary"
                        ghost
                        size="small"
                        icon={<FilePdfOutlined />}
                        loading={this.state.generatingReport}
                        disabled={!this.state.selectedTestCodes.length}
                        onClick={this.onGenerateReport}
                      >
                        Custom Report
                      </Button>
                      {moment().diff(moment(this.state.item.collectionDate), 'days') <= 7 && (
                        <Button type="primary" ghost size="small" onClick={() => this.onAddTest(this.state.item)}>
                          <SisternodeOutlined /> Add Test
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </Panel>
              <Panel
                header={<strong>Ordered Tests:</strong>}
                key="results"
                style={{ background: '#e6f4ff', borderRadius: 8}}
              >
                <Row gutter={24}>
                  <Col span={24}>
                    <p style={{ color: '#555', margin: '0 0 8px' }}>
                      Select the tests to include in the custom report.
                    </p>
                    <Table
                      columns={testColumns}
                      dataSource={this.state.item?.tests}
                      rowKey="code"
                      size="small"
                      pagination={false}
                      rowSelection={{
                        selectedRowKeys: this.state.selectedTestCodes,
                        onChange: (keys) => this.setState({ selectedTestCodes: keys }),
                      }}
                    />
                  </Col>
                </Row>
              </Panel>
            </Collapse>
            {this.state.addTest && (
              <Addon
                visible={this.state.addTest}
                onClose={() => this.setState({ addTest: false })}
                addonItem={this.state.addonItem}
              />
            )}
          </Fragment>
        }
        
        
      </Card>
    );
  }
}

export default function AccessionDetailWrapper(props) {
  const { accessionID } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  return <AccessionDetail {...props} accessionID={accessionID} navigate={navigate} screens={screens} />;
}